//1.0 Main controller than creates all sub controllers:
//Last resident of src/app/controller/ — the orchestrator that wires every
//other controller together (views/, chrome/, services/ all hang off it), so
//it belongs to no single view/chrome/service home and stays here.
import { globals } from 'config/globals';
import { Controller } from 'core/Controller';
import { Events } from 'core/events/Events';
import { AppEvents } from 'app/events/AppEvents';

import { ViewReadyEvent } from 'app/events/ViewReadyEvent';
import { PixiResourceLoadEvent } from 'app/events/PixiResourceLoadEvent';
import { AjaxHTMLLoadedEvent } from 'app/events/AjaxHTMLLoadedEvent';

import { HomeConfig } from 'app/views/home/config/HomeConfig';
import { ScreensWorkConfig, SpacesWorkConfig, SystemsWorkConfig } from 'app/views/work/config';

import { SectionType, DepartmentType, MenuStateType, AboutSectionType, ViewType } from 'app/data/Enums';

import { PixiController } from 'app/services/PixiController';
import { PreloadController } from 'app/chrome/preload/PreloadController';
import { UIController } from 'app/views/ui/UIController';
import { NavMenuController } from 'app/chrome/navmenu/NavMenuController';
import { ScrollbarController } from 'app/chrome/scrollbar/ScrollbarController';
import { HomeController } from 'app/views/home/HomeController';
import { WorkController } from 'app/views/work/WorkController';
import { ProjectController } from 'app/views/project/ProjectController';
import { MouseController } from 'app/chrome/mouse/MouseController';
import { VerticalDragController } from 'app/chrome/verticaldrag/VerticalDragController';

import { AboutController } from 'app/views/about/AboutController';
import { HistoryController } from 'app/services/HistoryController';
import { HistoryStateModel } from 'app/model/HistoryStateModel';

import DelayHelper from 'utils/timers';
import { ProjectsMenuController } from 'app/views/projectsmenu/ProjectsMenuController';
import SiteTransitions from 'app/services/SiteTransitions';
import { Simple } from 'utils/analytics';

export class AppController extends Controller {
	private _pixi: PixiController;
	private _preload: PreloadController;
	private _mouse: MouseController;
	private _verticalDrag: VerticalDragController;
	private _history: HistoryController;
	private _scrollbar: ScrollbarController;
	private _ui: UIController;
	private _navMenu: NavMenuController;
	private _home: HomeController;
	private _screens: WorkController;
	private _spaces: WorkController;
	private _systems: WorkController;
	private _project: ProjectController;
	private _about: AboutController;
	private _projectsMenu: ProjectsMenuController;

	//Active view and state:
	private _activeViewController: Controller;
	private _activeState: HistoryStateModel;


	public init(): void {
		this._setControllerRefs();
		this._setupTransitions();
		this._injectControllerRefs();
		this._addListeners();
		this._setSectionConfigs();
	}

	public DOMReady(): void {}

	public modulesMounted(): void {}

	public get pixi(): PIXI.Application {
		return this._pixi.getPixi();
	}

	//Private methods:
	//------------------------------------------------------------------------------------
	//-------------------------------------------------------------------------------------
	//-------------------------------------------------------------------------------------
	//-------------------------------------------------------------------------------------

	/** Controllers are created automatically through Core.controllers directive so just need to granb them */
	private _setControllerRefs(): void {
		this._pixi = this.get('Pixi');
		this._preload = this.get('Preload');
		this._mouse = this.get('Mouse');
		this._verticalDrag = this.get('VerticalDrag');
		this._history = this.get('History');
		this._scrollbar = this.get('Scrollbar');
		this._ui = this.get('UI');
		this._navMenu = this.get('NavMenu');
		this._home = this.get('Home');
		this._spaces = this.get('Spaces');
		this._screens = this.get('Screens');
		this._systems = this.get('Systems');
		this._project = this.get('Project');
		this._about = this.get('About');
		this._projectsMenu = this.get('ProjectsMenuController');

		this._spaces.setDepartment(DepartmentType.SPACES);
		this._screens.setDepartment(DepartmentType.SCREENS);
		this._systems.setDepartment(DepartmentType.SYSTEMS);
	}

	/** Setup controller to handle all transitions + timers */
	private _setupTransitions(): void {
		SiteTransitions.DOM = this.DOM;
		SiteTransitions.$body = document.querySelector('body');
		SiteTransitions.$home = document.querySelector('.home');
		SiteTransitions.$overlayBg = document.querySelector('.work-bg');
		SiteTransitions.homeController = this._home;
		SiteTransitions.screensController = this._screens;
		SiteTransitions.spacesController = this._spaces;
		SiteTransitions.systemsController = this._systems;
		SiteTransitions.projectController = this._project;
		SiteTransitions.scrollbarController = this._scrollbar;
		SiteTransitions.uiController = this._ui;
	}

	/** Pass required controller refs to each other */
	private _injectControllerRefs(): void {
		this._preload.setControllers(this._ui);
		this._pixi.setControllers(this);
		this._navMenu.setControllers(this._about, this._mouse, this._ui);
		this._ui.setControllers(this._scrollbar, this._mouse);
		this._spaces.setControllers(this._mouse, this._pixi, this._projectsMenu, this._ui, this._navMenu);
		this._screens.setControllers(this._mouse, this._pixi, this._projectsMenu, this._ui, this._navMenu);
		this._systems.setControllers(this._mouse, this._pixi, this._projectsMenu, this._ui, this._navMenu);
		this._home.setControllers(this._mouse, this._pixi, this._preload, this._ui);
		this._about.setControllers(this._scrollbar, this._verticalDrag, this._pixi, this._ui);
		this._project.setControllers(
			this._mouse,
			this._scrollbar,
			this._verticalDrag,
			this._pixi,
			this._screens,
			this._spaces,
			this._systems
		);
		this._verticalDrag.setControllers(this._mouse, this._scrollbar, this._ui);
		this._projectsMenu.setControllers(this, this._navMenu, this._mouse, this._ui, this._about);
		this._history.setControllers(this._scrollbar);
	}

	/** Pass PIXI dependecies on Work and Home controllers: */
	private _setPixiDepencies(): void {
		this._screens.setPixi(this._pixi.getPixi(), this._pixi.getBackground(), this._pixi.getWork(DepartmentType.SCREENS));
		this._spaces.setPixi(this._pixi.getPixi(), this._pixi.getBackground(), this._pixi.getWork(DepartmentType.SPACES));
		this._systems.setPixi(this._pixi.getPixi(), this._pixi.getBackground(), this._pixi.getWork(DepartmentType.SYSTEMS));
		this._home.setPixi(this._pixi.getPixi(), this._pixi.getHome());
	}

	/** Add all the major app wide style event listeners: */
	private _addListeners(): void {
		//Loading:
		Events.on(PixiResourceLoadEvent, this._onPixiResourceLoaded.bind(this));
		Events.on(AjaxHTMLLoadedEvent, this._onAjaxHTMLLoaded.bind(this));
		Events.on(ViewReadyEvent, this._onViewReady.bind(this));

		//Navigation / View changes:
		Events.on(AppEvents.SET_INTIAL_STATE, this._onSetIntialState.bind(this));
		Events.on(AppEvents.CHANGE_SECTION, this._onChangeSection.bind(this));
		Events.on(AppEvents.POP_HISTORY_STATE, this._onPopHistoryState.bind(this));
		Events.on(AppEvents.DEEP_LINK_LOADER_COMPLETE, this._onDeepLinkLoaderComplete.bind(this));

		//UI / NavMenu type events:
		Events.on(AppEvents.DEBUG_LOGO_PRESS, this._onDebugLogoPress.bind(this));
		Events.on(AppEvents.ACTIVE_DEPARTMENT_NAV_MENU_PRESS, this._onActiveDepartmentNavMenuPress.bind(this));
		Events.on(AppEvents.MENU_STATE_CHANGE, this._onMenuStateChange.bind(this));
		Events.on(AppEvents.RESET_NAV_MENU, this._onResetNavMenu.bind(this));

		Events.on('click', '.footer__insta', (e) => {
			Simple.event('social_click', { type: (e.target as HTMLElement).dataset.type });
		});

		this._listenForPageVisible();
	}

	private _pixiWasRunning = false;
	private _listenForPageVisible() {
		document.onvisibilitychange = () => {
			if (document.visibilityState === 'hidden') {
				this._pixiWasRunning = this._pixi.wasRunning();
				if (this._pixiWasRunning) {
					this._pixi.pause();
				}

				(window as any).appIsPaused = true;
			} else {
				if (this._pixiWasRunning) {
					this._pixi.unpause();
				}

				(window as any).appIsPaused = false;
			}
		};
	}

	//Pixi resource has loaded so pass that resource on the controllers that require it:
	private _onPixiResourceLoaded(resourceEvent: PixiResourceLoadEvent): void {
		//If background type then tell the background controller free to load the background items

		this._setPixiDepencies();

		if (resourceEvent.resource['home-particles']) {
			this._home.pixiParticlesLoaded(resourceEvent.resource['home-particles']);
		}

		if (resourceEvent.resource['work-particles']) {
			this._screens.pixiBackgroundsLoaded(resourceEvent.resource['screens-bg'], DepartmentType.SCREENS);
			this._spaces.pixiBackgroundsLoaded(resourceEvent.resource['spaces-bg'], DepartmentType.SPACES);
			this._systems.pixiBackgroundsLoaded(resourceEvent.resource['systems-bg'], DepartmentType.SYSTEMS);

			this._screens.pixiParticlesLoaded(resourceEvent.resource['work-particles']);
			this._spaces.pixiParticlesLoaded(resourceEvent.resource['work-particles']);
			this._systems.pixiParticlesLoaded(resourceEvent.resource['work-particles']);

			this._screens.pixiParticles32Loaded(resourceEvent.resource['work-particles-32']);
			this._spaces.pixiParticles32Loaded(resourceEvent.resource['work-particles-32']);
			this._systems.pixiParticles32Loaded(resourceEvent.resource['work-particles-32']);
		}
	}

	/** Extrnal HTML has been set so pass to the correct controller that can then use the resource as required */
	private _onAjaxHTMLLoaded(event: AjaxHTMLLoadedEvent): void {
		let id: string = event.loader.id;
		let html: string = event.loader.html;

		console.log('id id id', id);

		if (id === '/') {
			this._home.setHTML(html);
		} else if (
			id === '/info' ||
			id === '/studio' ||
			id === '/jobs' ||
			id === '/contact' ||
			id === '/what-we-do' ||
			id === '/our-process'
		) {
			let subSection: string = id.substring(1, id.length);
			this._about.setHTML(html, subSection as AboutSectionType);
		} else if (id === '/screens') {
			this._screens.setHTML(html);
		} else if (id === '/spaces') {
			this._spaces.setHTML(html);
		} else if (id === '/systems') {
			this._systems.setHTML(html);
		} else {
			//No other 'static' type pages left, so must be a project
			this._project.setHTML(id, html);
		}
	}

	/**
	 * Hand the bundled section configs (ex deploy/public/json/*.json, now
	 * typed TS — HomeConfig + views/work/config) to the view controllers.
	 * Runs synchronously during init: this replaces the old JSONLoadEvent
	 * push from the preloader's JSON fetches, and the controllers' own
	 * load-flag joins (HomeLoading/WorkLoading) don't care that the 'json'
	 * flag now arrives first — they still wait on dom + pixi.
	 *
	 * Shallow copies keep setJSON's `projects` filter/sort reassignment off
	 * the exported config objects.
	 *
	 * Lite mode (parity with the old handler): home/systems get NO config,
	 * and screens+spaces share one merged config whose projects list is
	 * spaces' then screens' (WorkController re-sorts by `order` anyway).
	 */
	private _setSectionConfigs(): void {
		if (window.mode == 'lite') {
			let merged = { ...ScreensWorkConfig, projects: SpacesWorkConfig.projects.concat(ScreensWorkConfig.projects) };
			this._screens.setJSON(merged);
			this._spaces.setJSON(merged);
		} else {
			this._screens.setJSON({ ...ScreensWorkConfig });
			this._spaces.setJSON({ ...SpacesWorkConfig });
			this._systems.setJSON({ ...SystemsWorkConfig });
			this._home.setJSON({ ...HomeConfig });
		}
	}

	/**Intial preload is complete. So can show the site */

	/**
	 * View is ready so can then show if the site/preloading is wairing on it
	 * @param {ViewReadyEvent} event
	 */

	private _preloaded: boolean = false;
	private _onViewReady(event: ViewReadyEvent): void {
		//Pass the event direct to the preload manager.
		//This checks if that view is being currently being waited on
		let isPreloadingViewReady: boolean = event.view == ViewType.ALL ? true : this._preload.viewReady(event);

		//If the active controller is loaded and been waiting on then can show the active controller
		if (!this._preloaded && isPreloadingViewReady) {
			this._preload.hide();
			this._activeViewController.reset(null, true);
			this._activeViewController.show(null, true);

			//showForDeepLink is an optional hook only some view controllers implement,
			//hence the feature-check + casts (Controller has no such member):
			if ((this._activeViewController as any).showForDeepLink) {
				(this._activeViewController as any).showForDeepLink();
			}

			this._preloaded = true;
		}
	}

	//** Set intial section / state / active view. Happens straight away, configured by the active 'view' as routed */
	private _onSetIntialState(stateObject: HistoryStateModel): void {
		//Tell the preloader what the intial state is (So it can decide what order to load assets)

		this._preload.setIntialState(stateObject);
		this._navMenu.setState(stateObject, false);

		if (stateObject.section === SectionType.WORK) {
			//subsection holds a department slug ('screens'/'spaces'/'systems') on work URLs:
			this._home.setWorkDepartmentType(stateObject.subsection as DepartmentType);
		}

		this._ui.setIntialState(stateObject);

		//Get the active view controller by id ref (home, info, screens, spaces, project)
		this._switchControllerToActive(stateObject);

		//Set the intial state. Will be reached when the users presses back through all history states
		this._history.setIntialState(stateObject);
	}

	/** On change section. This comes form  */
	private _onChangeSection(stateObject: HistoryStateModel, pushHistoryState: boolean = true): void {
		//Check if the new view is ready to view.
		(this._getControllerFromState(stateObject) as any).isReadyToView(stateObject);

		console.trace('stateObject', stateObject, 'pushHistoryState', pushHistoryState);

		let pushHistory = !stateObject.skipHistoryState && pushHistoryState;

		this._projectsMenu.hide(true);

		//Add the history state if not bypassed (because coming from HistoryState pop() ) :
		if (pushHistory) {
			SiteTransitions.startNormalTransition();
			this._history.push(stateObject, stateObject.title, stateObject.href);
		}

		//Set the UI / breadcrumbs / menu state:
		this._ui.setState(stateObject);
		this._navMenu.setState(stateObject);

		//If the menu is showing then hide (If configured on stateObject as don't want mess with navMenu > info processes):

		//Check if transitions are bypassed (Because the view has controlled its own state change internally):
		//------------------------------------------------------------------------
		if (stateObject.bypassTransitions) {
			this._switchControllerToActive(stateObject);
			return;
		}

		//Check if an internal view switch (For example in info moving bewteen tabs, caused by menu action):
		//------------------------------------------------------------------------
		if (this._activeState.section === stateObject.section && this._activeState.section === SectionType.ABOUT) {
			this._activeState = stateObject;
			(this._activeViewController as any).subsectionStateChange(stateObject);
			if (!this._activeState.fromMenu) {
				this._navMenu.reset();
			}
			return;
		}

		//Check if a deeplink style:
		//-------------------------------------------------------------------------

		if (stateObject.isDeepLink) {
			this._onDeepLink(stateObject);
			console.log('NOT READY----------');
			return;
		}

		//Organic / mixed transition style switch. I.E going from home > work. Or nav menu to about, or work slider > to project
		///-------------------------------------------------------------------------

		//Hide the current/old view. Pass the state object of next state, so the view can workout how to hide itself based on where its going next
		console.trace('this._activeViewController', this._activeViewController);
		this._activeViewController.hide(stateObject);

		//Set to new view. Then pass the old state in with show, so the new view can work out how to show itsself based on where its comeing from:
		let oldState: HistoryStateModel = this._activeState;
		this._switchControllerToActive(stateObject);

		if (!stateObject.skipNewControllerReset) {
			this._activeViewController.reset(oldState);
		}

		this._activeViewController.show(oldState);

		this._activeState = stateObject;
	}

	/** On change section via back / forward buttons */
	private _onPopHistoryState(stateObject: HistoryStateModel): void {
		//If the section type is the same then can just trigger. This only applies to 'info' at this point
		let menuToMenu =
			(this._activeState.section === SectionType.MENU && stateObject.section == SectionType.ABOUT) ||
			(this._activeState.section === SectionType.ABOUT && stateObject.section == SectionType.MENU);
		let isWorkToWork =
			this._activeState.section == SectionType.WORK && this._activeState.section === stateObject.section;

		console.log('AppController.HistoryStatePop() 0');

		if ((!isWorkToWork && this._activeState.section === stateObject.section) || menuToMenu) {
			this._activeState = stateObject;

			console.log('AppController.HistoryStatePop() 1');
			(this._activeViewController as any).subsectionStateChange(stateObject);
			return;
		}

		//Let project controller handle this transition if project > work
		if (this._activeState.section === SectionType.PROJECT && stateObject.section === SectionType.WORK) {
			(this._activeViewController as ProjectController).historyStateBackToWork();
			return;
		}

		//Let project controller handle this transition if project > work
		if (this._activeState.section === SectionType.WORK && stateObject.section === SectionType.PROJECT) {
			(this._activeViewController as WorkController).historyStateToProject(stateObject);
			return;
		}

		//Let project controller handle this transition if project > work
		let isToRichSection =
			stateObject.section === SectionType.PROJECT ||
			stateObject.section === SectionType.WORK ||
			stateObject.section === SectionType.HOME;
		if (
			(this._activeState.section === SectionType.MENU || this._activeState.section === SectionType.ABOUT) &&
			isToRichSection
		) {
			this._navMenu.leaveMenu(true);
		}

		//If coming from home/work/project then just simulate a menu press
		let isFromRichSection =
			this._activeState.section === SectionType.PROJECT ||
			this._activeState.section === SectionType.WORK ||
			this._activeState.section === SectionType.HOME;
		if (
			(stateObject.section === SectionType.ABOUT || (stateObject.subsection && stateObject.subsection === 'menu')) &&
			isFromRichSection
		) {
			this._navMenu.historyFromRichToMenu(stateObject);
			this._activeViewController = this._about;
			this._activeState = stateObject;
			console.log('AppController.HistoryStatePop() 3');
			return;
		}

		//Flag that its a deeplink change:
		stateObject.fromMenu = false;
		stateObject.bypassTransitions = false;

		//Treat as a deep link:
		console.log('AppController.HistoryStatePop() full');
		this._onChangeSection(stateObject, false);
	}

	/** Deep link to new section (Through history state, or far reaching nav action like spaces, screens or home link) */
	private _onDeepLink(stateObject: HistoryStateModel): void {
		//Freeze active controller, will decativate and hide the view:
		if (this._activeViewController) {
			(this._activeViewController as any).freeze();
		}

		//2. Set the the new controller to the one defined by the state, then pass that state back so view can be setup as required:
		this._switchControllerToActive(stateObject);

		//Show a quick preloader to mask:
		this._mouse.setDragCursorActive(false);
		if (this._activeViewController) {
			this._preload.showDeepLinkLoader(this._activeViewController);
		}
		this._ui.hide();
	}

	/** Switch controller/state to active  */
	private _switchControllerToActive(stateObject: HistoryStateModel): void {
		let doUnpause =
			this._activeState &&
			this._activeState.section == SectionType.ABOUT &&
			(stateObject.section == SectionType.HOME || stateObject.section == SectionType.WORK);

		//Get controller view ref, but when work pick between screens / spaces
		this._activeState = stateObject;
		this._activeViewController = this._getControllerFromState(stateObject);
		(this._activeViewController as any).setState(this._activeState);

		if (doUnpause) {
			this.unpauseActive();
		}

		console.trace('this._activeState.section', this._activeState.section, 'stateObject.section ', stateObject.section);
	}

	/** Get controller from history state */
	private _getControllerFromState(state: HistoryStateModel): Controller {
		let viewControllerRef: string = state.section;
		if (state.section === SectionType.WORK) {
			viewControllerRef = state.subsection;
		}
		return this.get(viewControllerRef);
	}

	//** History state loader complete. Essentiall snazzy animation is complete so ready to show section again */
	private _onDeepLinkLoaderComplete(): void {
		//Reset the view to an intial state as defined by the set state then show
		//(This will mean the view is under the preloaded and will show as it hides
		this._navMenu.reset();
		this._activeViewController.reset(null, true);
		this._activeViewController.show(null, true);
	}

	//** Logo press jump to home > middle */
	private _onDebugLogoPress(payload): void {
		//If the home section is active then animate to 0.5:
		if (this._activeState.section === SectionType.HOME || (payload && payload.isMenuHomeClose)) {
			this._home.unpause();
			this._home.animateProgressToValue(0);
			this._mouse.setDragCursorActive(true);
			this._navMenu.reset();

			Simple.event('debug_logo_press', { type: 'slide_on_home' });

			if (this._projectsMenu.isShowing()) {
				this._projectsMenu.hide(false);
			}

			return;
		}

		//Home is not showing so, so do hard deep link transition to the section:
		Simple.event('debug_logo_press', { type: 'reload' });
		window.location.href = !window.isHome ? '/' : '/?s=1';
		return;

		let stateObject: HistoryStateModel = {
			section: SectionType.HOME,
			title: 'Home',
			href: '/',
			isDeepLink: true,
			hideMenu: true
		};
		this._onChangeSection(stateObject);
	}

	/** On active deparment menu press. Basically just scroll back to start of the timeline. */
	private _onActiveDepartmentNavMenuPress(department: DepartmentType) {
		this._navMenu.reset();
		let viewController = department === DepartmentType.SCREENS ? this._screens : this._spaces;
		viewController.animateProgressToValue(0);
	}

	public pauseActive(direction: string = 'down') {
		this._activeViewController.pauseForMenu(direction);
		this._mouse.pause();
	}

	public unpauseActive() {
		this._activeViewController.unpause();
		this._mouse.unpause();

		if (globals.openingActionClicked) {
			this._mouse.setDragCursorActive(true);
			this._mouse.setHiddenState(false, true);
		}
	}

	/** When menu is opened / closed then need to pause/start whatever 'view' controller is active */
	private _onMenuStateChange(e: any): void {
		let state: MenuStateType = e.state;
		if (state === MenuStateType.OPEN) {
			this.pauseActive('side');
			this._mouse.pause();

			//Menu is opened. So its likely that sectiom will be opened next. So inject that HTML
			//Add slight delay so transition can happen without jank
			DelayHelper.setTimeout(() => this._about.que(), 1000);
		} else if (state === MenuStateType.CLOSED) {
			this._activeViewController.unpause();
			this._mouse.unpause();
			this._mouse.setHiddenState(false, true);
		}

		this._ui.setNavState(state);

		//Tracking
	}

	/** Reset the menu state on returning back to the  */
	private _onResetNavMenu(): void {
		this._navMenu.reset();
	}
}
